/* eslint-disable max-classes-per-file */
import { Model } from 'app/model/backendmodel';

import { Injectable } from '@angular/core';
import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import _ from 'lodash';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import {
    IDatasetTypeMetadata,
    IRegisteredDatasetTypeMetadata
} from 'app/extensibility/metadatas/resource-type/dataset-type-metadata';
import { DatasetRegistryService } from 'app/registries/dataset/dataset-registry.service';
import { Dataset } from 'app/model/dataset/dataset';
import { DatasetAdapter } from 'app/model/adapters/datasets/dataset.adapter';
import { CopySource } from 'app/model/pipeline/copysource/copysource';
import { CopySourceAdapter } from 'app/model/adapters/activities/copysource/copysource.adapter';

import { CopySink } from 'app/model/pipeline/copysink/copysink';
import { CopySinkAdapter } from 'app/model/adapters/activities/copysink/copysink.adapter';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { convertDynamicValidatorToConstraint } from '../utls/util';
import { CopySourceRegistryService } from './copy-source-registry.service';
import { CopySinkRegistryService } from './copy-sink-registry.service';
import { mapDatatypeToExpressionType } from 'app/services/datatype-to-expressiontype';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { IConnectorProperty } from 'app/services/dynamic-connector.service.models';
import { isStaticFormatTypeCopySourceOrSink, isStaticFormatDataset } from 'app/shared/utils/dataset-validation.util';
import { TabularTypeDataset } from 'app/model/dataset/tabular-type.dataset';
import { TabularTypeDatasetAdapter } from 'app/model/adapters/datasets/tabular-type.dataset.adapter';
import { TabularCopySource } from 'app/model/pipeline/copysource/tabular-type.copysource';
import { Owned } from 'app/model/base/named';
import { format } from 'app/shared/utils/string.util';
import { LinkedServiceUtils } from 'app/model/linkedservice/linked-service.utils';
import { BehaviorSubject } from 'rxjs';
import { sqlPoolLinkedServiceMetadata } from '../linked-service/sql-pool-connection-metadata';

@Injectable({
    providedIn: 'root'
})
export class DatasetDynamicLoaderService {
    public isInitialized$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(
        private readonly _datasetRegistry: DatasetRegistryService,
        private readonly _linkedServiceRegistryService: LinkedServiceRegistryService,
        private readonly _copySourceRegistry: CopySourceRegistryService,
        private readonly _copySinkRegistry: CopySinkRegistryService
    ) {}

    public initialize(metadatasInput: IDatasetTypeMetadata[]): void {
        if (this.isInitialized$.value) {
            return;
        }
        const metadatas: IRegisteredDatasetTypeMetadata[] = [];
        const lsMetadatas = this._linkedServiceRegistryService.getAllVisibleLinkedServiceConfigurations();
        // Special case 1: we need sqlPoolLinkedServiceMetadata (notShowInTypeSelection: true) to genrate dataset metadata (notShowInTypeSelection: false)
        if (__IS_A365__) {
            lsMetadatas.push(sqlPoolLinkedServiceMetadata);
        }
        const primarylsMetadatas = this._linkedServiceRegistryService.getAllPrimaryLinkedServiceConfigurations();

        // consider a linkedservice may be supported by several dataset, e.g. AzureBlob ls is supported both in Parquet, DelimitedText and AzureBlob dataset
        // let's make sure one linkedservice is pushed for only once.
        const registerdLinkedserviceTypeSet: Set<string> = new Set();
        metadatasInput.forEach((metadata) => {
            if (metadata.disabled) {
                return;
            }
            metadata.isCDCOnly = metadata.isCDCOnly != null ? metadata.isCDCOnly : false;
            metadatas.push({
                /**
                 * We will generate alias dataset metdata from linked service metadata, so setting notShowInTypeSelection as true for primary dataset metedata
                 */
                notShowInTypeSelection: true,
                ...metadata
            });

            metadata.supportLinkedServiceTypes.forEach((lsType) => {
                if (
                    metadata.type === adfModelTypeMap.relationalTable.datasetType &&
                    [
                        adfModelTypeMap.teradata.linkedServiceType,
                        adfModelTypeMap.sapHana.linkedServiceType,
                        adfModelTypeMap.sybase.linkedServiceType,
                        adfModelTypeMap.sapBw.linkedServiceType,
                        adfModelTypeMap.db2.linkedServiceType,
                        adfModelTypeMap.amazonRedshift.linkedServiceType,
                        adfModelTypeMap.odbc.linkedServiceType,
                        adfModelTypeMap.postgreSql.linkedServiceType,
                        adfModelTypeMap.mysql.linkedServiceType
                    ].indexOf(lsType) !== -1
                ) {
                    return;
                }

                if (
                    lsType === adfModelTypeMap.cosmosSql.linkedServiceType &&
                    metadata.type !== adfModelTypeMap.cosmosSql.datasetType
                ) {
                    return;
                }

                // Special case 2: Lakehouse file maps to both format-type datasets and lakehouseTable dataset
                const isLhDatasetWithLhLinkedService =
                    lsType === adfModelTypeMap.lakehouse.linkedServiceType &&
                    metadata.type === adfModelTypeMap.lakehouseTable.datasetType;
                if (registerdLinkedserviceTypeSet.has(lsType) && !isLhDatasetWithLhLinkedService) {
                    return;
                }
                registerdLinkedserviceTypeSet.add(lsType);
                const primaryMetadata = primarylsMetadatas.find((lsMetadata) => lsMetadata.type === lsType);
                lsMetadatas
                    .filter((lsMetadata) => lsMetadata.type === lsType)
                    .forEach((lsMetadata) => {
                        metadatas.push({
                            type: metadata.type,
                            displayName: isLhDatasetWithLhLinkedService
                                ? metadata.displayName
                                : lsMetadata.displayNameForDataset || lsMetadata.displayName,
                            displayNameForCreation: lsMetadata.displayNameForCreation,
                            tooltip: metadata.tooltip || lsMetadata.tooltip,
                            icon: lsMetadata.icon,
                            isAlias: true,
                            datasetKind: metadata.datasetKind,
                            supportLinkedServiceTypes: [],
                            isSource:
                                primaryMetadata &&
                                ((primaryMetadata.copySourceTypes && primaryMetadata.copySourceTypes.length > 0) ||
                                    !primaryMetadata.notSupportCopySource),
                            isSink:
                                primaryMetadata &&
                                (LinkedServiceUtils.hasSinkTypes(primaryMetadata) ||
                                    !primaryMetadata.notSupportCopySink),
                            category: lsMetadata.category,
                            linkedServiceType: lsMetadata.type,
                            isDataflowCompatible: metadata.isDataflowCompatible,
                            supportCreatedAsV2: metadata.supportCreatedAsV2,
                            supportDataFlowSource:
                                metadata.datasetKind === 'file'
                                    ? lsMetadata.supportDataFlowSource
                                    : metadata.supportDataFlowSource,
                            supportDataFlowSink:
                                metadata.datasetKind === 'file'
                                    ? lsMetadata.supportDataFlowSink
                                    : metadata.supportDataFlowSink
                        });
                    });
            });
        });

        this.loadMetadatas(metadatas);
        this.isInitialized$.next(true);
    }

    /* eslint-disable @typescript-eslint/no-explicit-any */
    public loadMetadatas(metadatas: IRegisteredDatasetTypeMetadata[]): void {
        metadatas.forEach((metadata) => {
            // for non-dynamically generated dataset
            if ((metadata.isFirstClass || metadata.isAlias) && !metadata.isDynamicallyGenerated) {
                this._datasetRegistry.register(metadata);
                return;
            }

            // for dynamically generated standalone dataset
            // After generated these models, dynamic generated one should behave this same as first class
            // alias metadata could be format dataset metadata generated by dynamic linkedservice, we don't treat it as first class
            // e.g. dynamic ABC linkedservice supports DelimitedTextDataset, then an alias DelimitedTextDataset metadata would be generated
            if (metadata.isDynamicallyGenerated && !metadata.isAlias) {
                metadata.isFirstClass = true;
            }

            // for dynamically generated format type dataset, need update supportedLinkedserviceTypes of the registered static format type dataset
            // then register this alias dataset
            if (metadata.isDynamicallyGenerated && metadata.isAlias && isStaticFormatDataset(metadata.type)) {
                const primaryDatasetMetadata = this._datasetRegistry.getDatasetConfiguration(metadata.type);
                if (primaryDatasetMetadata != null) {
                    if (primaryDatasetMetadata.supportLinkedServiceTypes != null) {
                        primaryDatasetMetadata.supportLinkedServiceTypes.push(metadata.linkedServiceType);
                    } else {
                        primaryDatasetMetadata.supportLinkedServiceTypes = [metadata.linkedServiceType];
                    }
                    if (!this._datasetRegistry.hasAliasDataset(metadata.linkedServiceType)) {
                        this._datasetRegistry.register(metadata);
                    }
                }
                return;
            }

            // for dynamically generated standalone dataset
            if (this._datasetRegistry.has(metadata.type)) {
                return;
            }

            this._datasetRegistry.register(metadata);

            const datasetAndAdapterModel = this._registerDatasetModels(metadata);

            this._registerCopySourceModels(metadata, datasetAndAdapterModel[0]);

            this._registerCopySinkModels(metadata, datasetAndAdapterModel[0]);
        });
    }

    private _getDynamicAdapts(properties: IConnectorProperty[]): {} {
        const adapts = {};
        if (properties) {
            properties.forEach(
                (property) => ((adapts as any)[property.propertyName] = property.isSensitive ? 'secret' : '*')
            );
        }
        return adapts;
    }

    private _generateDatasetAdapts(metadata: IDatasetTypeMetadata, staticAdapts?: {}): any {
        const adapts = {
            name: '*',
            properties: {
                type: '*',
                parameters: '*',
                description: '*',
                annotation: '*',
                folder: '*',
                linkedServiceName: '*',
                schema: '*',
                typeProperties: _.merge(
                    this._getDynamicAdapts(metadata.properties),
                    staticAdapts != null ? staticAdapts : {}
                )
            }
        };

        return adapts;
    }

    private _registerDatasetModels(metadata: IDatasetTypeMetadata): [any, any] {
        let datasetModelClass: any;
        let datasetAdapterClass: any;

        // tabular and hierarchical dataset with table property, inherit from tabular dataset
        if (metadata.datasetKind === 'table' || metadata.datasetKind === 'hierarchical') {
            datasetModelClass = class extends TabularTypeDataset {
                public static readonly MODEL: IModel;

                constructor(name?: string, description?: string) {
                    super(name, description);
                    this.isV2 = true;
                }
            };

            if (metadata.properties != null) {
                metadata.properties.forEach((p) => {
                    // we don't register table and schema as they're included in TabularTypeDataset
                    const constraint =
                        p.validators != null ? convertDynamicValidatorToConstraint(p.validators) : undefined;
                    Metamodel.attribute({
                        display: p.displayName,
                        association: p.propertyName.toUpperCase(),
                        templatable: p.isParameterizable,
                        accepts: mapDatatypeToExpressionType(p.dataType),
                        constraint
                    })(datasetModelClass.prototype, p.propertyName);
                });
            }
            datasetModelClass =
                Metamodel.model({
                    name: metadata.type,
                    display: [
                        format(stringResources.ModelName_DatasetTemplate, metadata.displayName),
                        format(stringResources.ModelName_DatasetsTemplate, metadata.displayName)
                    ]
                })(datasetModelClass) || datasetModelClass;

            datasetAdapterClass = class extends TabularTypeDatasetAdapter<TabularTypeDataset, Model.DatasetResource> {
                protected async _toResource(dataset: TabularTypeDataset): Promise<Model.DatasetResource> {
                    const modelDatasetResource = await super._toResourceInternal(
                        dataset,
                        metadata.type,
                        metadata.tableNameAlias,
                        metadata.schemaAlias,
                        metadata.tableAlias
                    );
                    const modelDataset = modelDatasetResource.properties;
                    metadata.properties?.forEach((p) => {
                        (modelDataset.typeProperties as any)[p.propertyName] = ValueAdapter.toResource(
                            dataset,
                            Metamodel.modelFor(datasetModelClass).attribute(p.propertyName)
                        );
                    });

                    if (dataset.raw) {
                        const parsed = JSON.parse(dataset.raw);
                        _.merge(modelDatasetResource, parsed);
                    }

                    return modelDatasetResource;
                }

                protected async _toModel(
                    modelDatasetResource: Model.DatasetResource,
                    dataset?: TabularTypeDataset
                ): Promise<TabularTypeDataset> {
                    const resultModel = await super._toModelInternal(
                        modelDatasetResource,
                        dataset,
                        datasetModelClass.MODEL,
                        metadata.tableNameAlias,
                        metadata.schemaAlias,
                        metadata.tableAlias
                    );
                    const datasetResource = modelDatasetResource.properties as Model.Dataset;
                    if (datasetResource && datasetResource.typeProperties != null) {
                        metadata.properties?.forEach((p) => {
                            ValueAdapter.toModel(
                                resultModel,
                                Metamodel.modelFor(datasetModelClass).attribute(p.propertyName),
                                datasetResource.typeProperties[p.propertyName]
                            );
                        });
                    }

                    resultModel.raw = JSON.stringify(
                        await ValueAdapter.remainder(this.context, modelDatasetResource, datasetModelClass.MODEL)
                    );

                    return resultModel;
                }
            };

            datasetAdapterClass =
                AdapterRegistry.adapter({
                    model: Metamodel.modelFor(datasetModelClass),
                    asynchronous: true,
                    adapts: this._generateDatasetAdapts(metadata, {
                        [metadata.tableNameAlias || 'tableName']: '*',
                        [metadata.tableAlias || 'table']: '*',
                        [metadata.schemaAlias || 'schema']: '*'
                    })
                })(datasetAdapterClass) || datasetAdapterClass;
        } else {
            datasetModelClass = class extends Dataset {
                public static readonly MODEL: IModel;

                constructor(name?: string, description?: string) {
                    super(name, description);
                    this.isV2 = true;
                }
            };

            if (metadata.properties != null) {
                metadata.properties.forEach((p) => {
                    const constraint =
                        p.validators != null ? convertDynamicValidatorToConstraint(p.validators) : undefined;
                    Metamodel.attribute({
                        display: p.displayName,
                        association: p.propertyName.toUpperCase(),
                        templatable: p.isParameterizable,
                        accepts: mapDatatypeToExpressionType(p.dataType),
                        constraint
                    })(datasetModelClass.prototype, p.propertyName);
                });
            }
            datasetModelClass =
                Metamodel.model({
                    name: metadata.type,
                    display: [
                        format(stringResources.ModelName_DatasetTemplate, metadata.displayName),
                        format(stringResources.ModelName_DatasetsTemplate, metadata.displayName)
                    ]
                })(datasetModelClass) || datasetModelClass;

            datasetAdapterClass = class extends DatasetAdapter<Dataset, Model.DatasetResource> {
                protected async _toResource(dataset: Dataset): Promise<Model.DatasetResource> {
                    const modelDatasetResource = (await super._toResource(dataset)) as Model.DatasetResource;
                    const modelDataset = this.properties(modelDatasetResource) as Model.Dataset;
                    modelDataset.type = metadata.type;
                    modelDataset.schema = this.columnValueToResource(dataset, dataset.columns);
                    modelDataset.typeProperties = {};
                    metadata.properties?.forEach((p) => {
                        (modelDataset.typeProperties as any)[p.propertyName] = ValueAdapter.toResource(
                            dataset,
                            Metamodel.modelFor(datasetModelClass).attribute(p.propertyName)
                        );
                    });

                    if (dataset.raw) {
                        const parsed = JSON.parse(dataset.raw);
                        _.merge(modelDatasetResource, parsed);
                    }

                    return modelDatasetResource;
                }

                protected async _toModel(
                    modelDatasetResource: Model.DatasetResource,
                    dataset?: Dataset
                ): Promise<Dataset> {
                    if (!dataset) {
                        dataset = Metamodel.modelFor(datasetModelClass).create<Dataset>(modelDatasetResource.name);
                    }
                    await super._toModel(modelDatasetResource, dataset);
                    const model = modelDatasetResource.properties as Model.Dataset;
                    if (model) {
                        // we assume all dynamic datasets are v2 datasets
                        dataset.isV2 = true;
                        dataset.columns = this.columnResourceToValue(dataset, model.schema);
                        if (model.typeProperties != null) {
                            metadata.properties?.forEach((p) => {
                                ValueAdapter.toModel(
                                    dataset,
                                    Metamodel.modelFor(datasetModelClass).attribute(p.propertyName),
                                    model.typeProperties[p.propertyName]
                                );
                            });
                        }
                    }

                    dataset.raw = JSON.stringify(
                        await ValueAdapter.remainder(this.context, modelDatasetResource, datasetModelClass.MODEL)
                    );

                    return dataset;
                }
            };

            datasetAdapterClass =
                AdapterRegistry.adapter({
                    model: Metamodel.modelFor(datasetModelClass),
                    asynchronous: true,
                    adapts: this._generateDatasetAdapts(metadata)
                })(datasetAdapterClass) || datasetAdapterClass;

            import('app/model/adapters/adapter-loader').then((m) => {
                m.AdapterLoader.adapters.push(datasetAdapterClass);
            });
        }
        return [datasetModelClass, datasetAdapterClass];
    }

    private _registerCopySourceModels(metadata: IDatasetTypeMetadata, datasetModelClass: any): void {
        // for copy source
        if (metadata.supportedCopySourceTypes != null) {
            metadata.supportedCopySourceTypes.forEach((sourceType) => {
                if (isStaticFormatTypeCopySourceOrSink(sourceType) || this._copySourceRegistry.has(sourceType)) {
                    return;
                }
                this._copySourceRegistry.register(sourceType);
                let copySourceClass =
                    metadata.datasetKind === 'table' || metadata.datasetKind === 'hierarchical'
                        ? class extends TabularCopySource {}
                        : class extends CopySource {};
                const isTabularCopySource = this._isTabularCopySourceClass(copySourceClass);
                if (metadata.copySourceProperties != null) {
                    metadata.copySourceProperties.forEach((p) => {
                        const constraint =
                            p.validators != null ? convertDynamicValidatorToConstraint(p.validators) : undefined;
                        Metamodel.attribute({
                            display: p.displayName,
                            association: p.propertyName.toUpperCase(),
                            templatable: p.isParameterizable,
                            accepts: mapDatatypeToExpressionType(p.dataType),
                            constraint
                        })(copySourceClass.prototype, p.propertyName);
                    });
                }
                copySourceClass =
                    Metamodel.model({
                        name: sourceType,
                        tags: { datasetModel: Metamodel.modelFor(datasetModelClass as any) },
                        display: []
                    })(copySourceClass) || copySourceClass;

                let copySourceAdapter = class extends CopySourceAdapter<CopySource, Model.CopySource> {
                    protected override async _toResource(copySource: CopySource): Promise<Model.CopySource> {
                        const result: Model.CopySource = isTabularCopySource
                            ? this._toTabularResource(
                                  copySource as TabularCopySource,
                                  Metamodel.modelFor(copySourceClass).name,
                                  metadata.queryAlias
                              )
                            : this.getCommonCopySourceProperties(copySource, sourceType);
                        metadata.copySourceProperties?.forEach((p) => {
                            result[p.propertyName] = ValueAdapter.toResource(
                                copySource,
                                Metamodel.modelFor(copySourceClass).attribute(p.propertyName)
                            );
                        });

                        if (copySource.raw) {
                            const parsed = JSON.parse(copySource.raw);
                            _.merge(result, parsed);
                        }

                        return result;
                    }

                    protected override async _toModel(
                        copySourceResource: Model.CopySource,
                        copyActivity: CopyActivity
                    ): Promise<CopySource> {
                        const result = isTabularCopySource
                            ? this._toTabularModel(
                                  copySourceResource,
                                  copyActivity,
                                  Metamodel.modelFor(copySourceClass),
                                  metadata.queryAlias
                              )
                            : this.createCopySourceWithCommonProperties(
                                  copySourceClass.MODEL,
                                  copySourceResource,
                                  copyActivity
                              );
                        metadata.copySourceProperties?.forEach((p) => {
                            ValueAdapter.toModel(
                                result,
                                Metamodel.modelFor(copySourceClass).attribute(p.propertyName),
                                copySourceResource[p.propertyName]
                            );
                        });

                        result.raw = JSON.stringify(
                            await ValueAdapter.remainder(this.context, copySourceResource, copySourceClass.MODEL)
                        );
                        return result;
                    }
                };

                copySourceAdapter =
                    AdapterRegistry.adapter({
                        model: Metamodel.modelFor(copySourceClass),
                        asynchronous: true,
                        adapts: {
                            type: '*',
                            [metadata.queryAlias || 'query']: '*',
                            ...this._getDynamicAdapts(metadata.copySourceProperties)
                        }
                    })(copySourceAdapter) || copySourceAdapter;

                import('app/model/adapters/adapter-loader').then((m) => {
                    m.AdapterLoader.adapters.push(copySourceAdapter);
                });
            });
        }
    }

    private _registerCopySinkModels(metadata: IDatasetTypeMetadata, datasetModelClass: any): void {
        // for copy sink
        if (metadata.supportedCopySinkTypes != null) {
            metadata.supportedCopySinkTypes.forEach((sinkType) => {
                if (isStaticFormatTypeCopySourceOrSink(sinkType) || this._copySinkRegistry.has(sinkType)) {
                    // todo, bind dataset to it
                    return;
                }
                this._copySinkRegistry.register(sinkType);
                let copySinkClass = class extends CopySink {};
                if (metadata.copySinkProperties != null) {
                    metadata.copySinkProperties.forEach((p) => {
                        const constraint =
                            p.validators != null ? convertDynamicValidatorToConstraint(p.validators) : undefined;
                        Metamodel.attribute({
                            display: p.displayName,
                            association: p.propertyName.toUpperCase(),
                            templatable: p.isParameterizable,
                            accepts: mapDatatypeToExpressionType(p.dataType),
                            constraint
                        })(copySinkClass.prototype, p.propertyName);
                    });
                }
                copySinkClass =
                    Metamodel.model({
                        name: sinkType,
                        tags: { datasetModel: Metamodel.modelFor(datasetModelClass) },
                        display: []
                    })(copySinkClass) || copySinkClass;
                let copySinkAdapter = class extends CopySinkAdapter<CopySink, Model.CopySink> {
                    protected async _toResource(copySink: CopySink): Promise<Model.CopySink> {
                        const result: Model.CopySink = {
                            type: sinkType
                        };
                        metadata.copySinkProperties?.forEach((p) => {
                            result[p.propertyName] = ValueAdapter.toResource(
                                copySink,
                                Metamodel.modelFor(copySinkClass).attribute(p.propertyName)
                            );
                        });
                        if (copySink.raw) {
                            const parsed = JSON.parse(copySink.raw);
                            _.merge(result, parsed);
                        }

                        return result;
                    }

                    protected async _toModel(copySinkResource: Model.CopySink, owner: CopyActivity): Promise<CopySink> {
                        const result = Owned.create<CopySink>(Metamodel.modelFor(copySinkClass), owner);
                        metadata.copySinkProperties?.forEach((p) => {
                            ValueAdapter.toModel(
                                result,
                                Metamodel.modelFor(copySinkClass).attribute(p.propertyName),
                                copySinkResource[p.propertyName]
                            );
                        });
                        result.raw = JSON.stringify(
                            await ValueAdapter.remainder(this.context, copySinkResource, copySinkClass.MODEL)
                        );

                        return result;
                    }
                };
                copySinkAdapter =
                    AdapterRegistry.adapter({
                        model: Metamodel.modelFor(copySinkClass),
                        asynchronous: true,
                        adapts: {
                            type: '*',
                            ...this._getDynamicAdapts(metadata.copySinkProperties)
                        }
                    })(copySinkAdapter) || copySinkAdapter;

                import('app/model/adapters/adapter-loader').then((m) => {
                    m.AdapterLoader.adapters.push(copySinkAdapter);
                });
            });
        }
    }

    private _isTabularCopySourceClass(copySourceClass: any): boolean {
        return (
            Object.getPrototypeOf(copySourceClass.prototype).constructor.name ===
            TabularCopySource.prototype.constructor.name
        );
    }
}
